import * as THREE from 'three'
import { OrbitControls } from 'three/addons/controls/OrbitControls.js'
import GUI from 'lil-gui'

import vertexShader from './shaders/particles/vertex.glsl'
import fragmentShader from './shaders/particles/fragment.glsl'

/**
 * BASE
 */
const canvas = document.querySelector('canvas.webgl')
const scene = new THREE.Scene()
// const gui = new GUI({ width: 320 })

const sizes = {
    width: window.innerWidth,
    height: window.innerHeight,
    pixelRatio: Math.min(window.devicePixelRatio, 2)
}

const isMobile = () => window.matchMedia('(max-width: 768px)').matches

/**
 * CAMERA
 */
const camera = new THREE.PerspectiveCamera(40, sizes.width / sizes.height, 0.1, 100)
scene.add(camera)

const cameraTarget = { x: -0.3, y: 0.5, z: 0 }

/**
 * RENDERER
 */
const renderer = new THREE.WebGLRenderer({
    canvas,
    antialias: true
})
renderer.setSize(sizes.width, sizes.height)
renderer.setPixelRatio(sizes.pixelRatio)
renderer.setClearColor('#0A0A10')

/**
 * CONTROLS (disabled)
 */
const controls = new OrbitControls(camera, canvas)
controls.enabled = false

/**
 * MATERIAL
 */
const material = new THREE.ShaderMaterial({
    vertexShader,
    fragmentShader,
    uniforms: {
        uTime: { value: 0 },
        uSize: { value: 0.02 },
        uResolution: { value: new THREE.Vector2(
            sizes.width * sizes.pixelRatio,
            sizes.height * sizes.pixelRatio
        )},
        uMouse: { value: new THREE.Vector2(9999, 9999) },

        uFrequency: { value: new THREE.Vector2(0.5, 0.85) },
        uElevation: { value: 1.4 },
        uSpeed: { value: 0.14 },

        uNoiseFrequency: { value: 0.12 },
        uNoiseSpeed: { value: 0.2 },
        uNoiseFlow: { value: 0.1 },

        uInteractState: { value: 0 },
        uPointIntensity: { value: 0.65 }
    },
    transparent: true,
    depthWrite: false,
    blending: THREE.AdditiveBlending
})

/**
 * GUI — POINTS
 */
// const pointFolder = gui.addFolder('Points')
// pointFolder.add(material.uniforms.uSize, 'value', 0.03, 0.12, 0.001).name('Point Size')
// pointFolder.add(material.uniforms.uPointIntensity, 'value', 0.3, 1.2, 0.01).name('Brightness')
// pointFolder.open()

// const waveFolder = gui.addFolder('Wave')

// waveFolder
//   .add(material.uniforms.uSpeed, 'value', 0.02, 0.4, 0.001)
//   .name('Wave Speed')

// waveFolder.open()


/**
 * GRID → POINTS
 */
const GRID_X = 160
const GRID_Z = 160
const COUNT = GRID_X * GRID_Z

const positions = new Float32Array(COUNT * 3)
let i = 0

for (let x = 0; x < GRID_X; x++) {
    for (let z = 0; z < GRID_Z; z++) {
        positions[i * 3 + 0] = (x / (GRID_X - 1) - 0.5) * 12
        positions[i * 3 + 1] = 0
        positions[i * 3 + 2] = (z / (GRID_Z - 1) - 0.5) * 12
        i++
    }
}

const geometry = new THREE.BufferGeometry()
geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3))

const points = new THREE.Points(geometry, material)
scene.add(points)

/**
 * RESPONSIVE PRESET
 */
const applyResponsivePreset = () => {
    if (isMobile()) {
        camera.position.set(4.8, 1.4, 11.5)
        cameraTarget.x = -0.2
        cameraTarget.y = 0.9

        material.uniforms.uSize.value = 0.02
        material.uniforms.uElevation.value = 3.1
        material.uniforms.uSpeed.value = 0.12
        material.uniforms.uMouse.value.set(9999, 9999)
    } else {
        camera.position.set(5.9, 0.98, 7.36)
        cameraTarget.x = -0.3
        cameraTarget.y = 0.5

        material.uniforms.uSize.value = 0.02
        material.uniforms.uElevation.value = 1.4
        material.uniforms.uSpeed.value = 0.08
    }
}

applyResponsivePreset()

/**
 * GUI — CAMERA
 */
// const cameraFolder = gui.addFolder('Camera')
// cameraFolder.add(camera.position, 'x', -10, 10, 0.01)
// cameraFolder.add(camera.position, 'y', -10, 10, 0.01)
// cameraFolder.add(camera.position, 'z', 0, 20, 0.01)

// const targetFolder = cameraFolder.addFolder('Look At')
// targetFolder.add(cameraTarget, 'x', -5, 5, 0.01)
// targetFolder.add(cameraTarget, 'y', -5, 5, 0.01)
// targetFolder.add(cameraTarget, 'z', -5, 5, 0.01)

// cameraFolder.open()

/**
 * MOUSE
 */
const mouse = new THREE.Vector2(9999, 9999)
window.addEventListener('mousemove', (e) => {
    if (isMobile()) return
    const rect = canvas.getBoundingClientRect()
    mouse.x = ((e.clientX - rect.left) / rect.width) * 2 - 1
    mouse.y = -((e.clientY - rect.top) / rect.height) * 2 + 1
})

/**
 * RESIZE
 */
window.addEventListener('resize', () => {
    sizes.width = window.innerWidth
    sizes.height = window.innerHeight
    sizes.pixelRatio = Math.min(window.devicePixelRatio, 2)

    camera.aspect = sizes.width / sizes.height
    camera.updateProjectionMatrix()

    renderer.setSize(sizes.width, sizes.height)
    renderer.setPixelRatio(sizes.pixelRatio)

    material.uniforms.uResolution.value.set(
        sizes.width * sizes.pixelRatio,
        sizes.height * sizes.pixelRatio
    )

    applyResponsivePreset()
})

/**
 * ANIMATE
 */
const clock = new THREE.Clock()

const tick = () => {
    material.uniforms.uTime.value = clock.getElapsedTime()
    material.uniforms.uMouse.value.copy(mouse)

    camera.lookAt(cameraTarget.x, cameraTarget.y, cameraTarget.z)
    renderer.render(scene, camera)

    requestAnimationFrame(tick)
}

tick()
